import classNames from 'classnames';
import memoize from 'memoize-one';
import { PureComponent } from 'react';

import getDurations from 'my-core/curriculum/getDurations';
import { approximateDuration } from 'my-core/date-utils';
import {
  allChildren,
  filterOnlineCourseContentsByQuery,
  findAncestor,
  findChild,
  renderContentMap,
} from 'my-core/online-course-content-map';
import { onlineCoursePath } from 'my-core/routes';

import LoginProtected from 'my-components/LoginProtected';

import Activity from './Activity';
import Chapter from './Chapter';
import Section from './Section';

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { faBookmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export default class CourseCurriculum extends PureComponent {
  getFilteredContent = memoize(filterOnlineCourseContentsByQuery);

  render() {
    const {
      activeComponent,
      ActivityProps,
      basePath,
      canPreviewAll: canPreviewAllProp,
      canViewAll,
      ChapterProps,
      displayMeta,
      eventTrackingProperties,
      filter,
      onlineCourseContent,
      onlineCourseUser,
      onLinkClick,
      onlyFaves,
      onSetFavorite,
      practiceMode,
      practiceModeSectionQuestionCounts,
      practiceModeSectionResults,
      rootClassName,
      SectionProps,
      skipChapter,
      tokenPurchases,
      tourId,
    } = this.props;

    if (onlyFaves)
      return (
        <div className={rootClassName} css={rootCss}>
          {this.renderFavoriteActivities()}
        </div>
      );

    const canPreviewAll = canViewAll || canPreviewAllProp;

    const durations = getDurations(onlineCourseContent);

    const activeChapter = findAncestor(activeComponent, n => n.type === 'chapter');
    const activeSection = findAncestor(activeComponent, n => n.type === 'section');

    function getComponentDuration(component) {
      return (
        component.fullPath
          .replace(new RegExp(`^${onlineCourseContent.fullPath || ''}`), '')
          .split('/')
          .filter(Boolean)
          .reduce((m, k) => m.children[k], durations)?.total || 0
      );
    }

    if (!onlineCourseContent.children.length) return null;

    const filteredOnlineCourseContent = this.getFilteredContent(onlineCourseContent, filter);

    return (
      <div className={rootClassName} css={rootCss}>
        {renderContentMap(filteredOnlineCourseContent, (component, renderChildren) => {
          switch (component.type) {
            case 'root':
            case 'group':
              if (!practiceMode && component.key === 'mock-exam' && component.children.length) {
                return (
                  <Chapter
                    key="ME"
                    component={component}
                    defaultExpanded={component === activeComponent?.parent}
                    eventTrackingProperties={eventTrackingProperties}
                    hasPreview={canPreviewAll && component.children.some(a => a.data.can_preview)}
                    renderChildren={renderChildren}
                    title={`Mock Exam${component.children.length > 1 ? 's' : ''}`}
                    tourId={tourId}
                    {...ChapterProps}
                  />
                );
              }
              return renderChildren();
            case 'activity': {
              if (practiceMode) return;
              const activity = (
                <Activity
                  key={component.key}
                  active={activeComponent?.data?.id === component.data.id}
                  activity={component.data}
                  canPreviewAll={canPreviewAll}
                  canViewAll={canViewAll}
                  component={component}
                  displayMeta={displayMeta}
                  eventTrackingProperties={eventTrackingProperties}
                  onlineCourseUser={onlineCourseUser}
                  onLinkClick={onLinkClick}
                  onSetFavorite={onSetFavorite}
                  path={`${basePath}${component.fullPath}`}
                  showViewableIcon={!canViewAll}
                  title={`${component.fullPos}. ${component.data.title || ''}`}
                  tokenPurchases={tokenPurchases}
                  topLevel={component.parent.type === 'root'}
                  {...ActivityProps}
                />
              );
              if (component.data.type === 'checkpoint_quiz') {
                return (
                  <Section
                    key={component.key}
                    alwaysExpanded
                    eventTrackingProperties={eventTrackingProperties}
                    renderChildren={() => activity}
                    title="Checkpoint Quiz"
                    {...SectionProps}
                  />
                );
              }
              return activity;
            }
            case 'section': {
              let metaText;
              if (practiceMode) {
                const questionCount = practiceModeSectionQuestionCounts?.[component.data.id];
                const sectionResult = practiceModeSectionResults?.[component.data.id];
                metaText = `${sectionResult?.attempted || 0}/${questionCount || 0}`;
              } else {
                const activities = component.children;
                const activityIds = activities.map(a => a.data.id);
                const activitiesCompleted = (onlineCourseUser?.progress?.completed || []).filter(
                  p => p.is_completed && activityIds.includes(p.activity_id),
                ).length;
                metaText = activities.length ? `${activitiesCompleted}/${activities.length}` : undefined;
              }
              const hasPreview = !practiceMode && !canViewAll && canPreviewAll && component.data.can_preview;
              // !!(findAncestor(component, n => n.data.can_preview) || findChild(component, c => c.data.can_preview));
              // !findAncestor(component.parent, n => n.data.can_preview) &&
              // (component.data.can_preview || findChild(component, c => c.data.can_preview));

              return (
                <Section
                  key={component.key}
                  active={practiceMode && activeComponent?.data?.id === component.data.id}
                  defaultExpanded={component === activeSection || (!activeSection && component.childIndex === 0)}
                  disabled={
                    practiceMode ? !practiceModeSectionQuestionCounts?.[component.data.id] : !component.children.length
                  }
                  eventTrackingProperties={eventTrackingProperties}
                  hasPreview={hasPreview}
                  metaText={metaText}
                  path={practiceMode && `${basePath}/practice-mode${component.fullPath}`}
                  practiceMode={practiceMode}
                  renderChildren={practiceMode ? undefined : renderChildren}
                  title={`${component.fullPos}. ${component.data.title || ''}`}
                  {...SectionProps}
                />
              );
            }
            case 'chapter': {
              if (skipChapter) return renderChildren();

              const chapterDuration = !practiceMode && displayMeta && getComponentDuration(component);
              const hasPreview = !canViewAll && canPreviewAll && !!component.data.can_preview;
              // !!(component.data.can_preview || findChild(component, c => c.data.can_preview));
              return (
                <Chapter
                  key={component.key}
                  active={component.data.id === activeChapter?.data?.id}
                  component={component}
                  defaultExpanded={component === activeChapter}
                  disabled={
                    practiceMode ?
                      !findChild(component, c => c.type === 'section' && practiceModeSectionQuestionCounts[c.data.id])
                    : !findChild(component, c => c.type === 'activity' || c.type === 'section')
                  }
                  eventTrackingProperties={eventTrackingProperties}
                  hasPreview={hasPreview}
                  metaText={
                    !!chapterDuration &&
                    approximateDuration(chapterDuration, { shortName: true, excludeDays: true }).join('')
                  }
                  renderChildren={renderChildren}
                  title={`${component.pos}. ${component.data.title || ''}`}
                  tourId={tourId}
                  {...ChapterProps}
                />
              );
            }
          }
        })}
      </div>
    );
  }

  renderFavoriteActivities() {
    const {
      activeComponent,
      ActivityProps,
      basePath,
      bookmarkIconClassName,
      canPreviewAll: canPreviewAllProp,
      canViewAll,
      displayMeta,
      eventTrackingProperties,
      noFavoritesClassName,
      onlineCourse,
      onlineCourseContent,
      onlineCourseUser,
      onLinkClick,
      onSetFavorite,
      tokenPurchases,
      tourId,
    } = this.props;

    const canPreviewAll = canViewAll || canPreviewAllProp;
    const activityProgresses = onlineCourseUser?.progress?.completed;
    const favoriteActivities =
      activityProgresses?.length &&
      allChildren(onlineCourseContent).filter(
        n => n.type === 'activity' && activityProgresses.some(p => p.starred && p.activity_id === n.data.id),
      );

    if (!favoriteActivities?.length) {
      return (
        <div className={classNames('CC-noFavorites', noFavoritesClassName)} id={tourId}>
          <Typography gutterBottom>No bookmarks yet!</Typography>
          {!onlineCourseUser ?
            <>
              <Typography
                sx={{
                  color: 'text.secondary',
                  marginBottom: '16px',
                }}
                variant="body2"
              >
                Sign up for a free account to bookmark activities to come back to later
              </Typography>
              <LoginProtected>
                <Button size="small" variant="contained">
                  Sign Up
                </Button>
              </LoginProtected>
            </>
          : <Typography
              sx={{
                color: 'text.secondary',
              }}
              variant="body2"
            >
              Click on a{' '}
              <FontAwesomeIcon className={classNames('CC-bookmarkIcon', bookmarkIconClassName)} icon={faBookmark} /> in
              the course curriculum to bookmark your first activity
            </Typography>
          }
        </div>
      );
    }

    return favoriteActivities.map(component => (
      <Activity
        key={component.data.id}
        active={activeComponent?.data?.id === component.data.id}
        activity={component.data}
        canPreviewAll={canPreviewAll}
        canViewAll={canViewAll}
        component={component}
        displayMeta={displayMeta}
        eventTrackingProperties={eventTrackingProperties}
        isFavorites
        onlineCourseUser={onlineCourseUser}
        onLinkClick={onLinkClick}
        onSetFavorite={onSetFavorite}
        path={`${basePath || onlineCoursePath(onlineCourse)}${component.fullPath}`}
        title={`${component.fullPos}. ${component.data.title}`}
        tokenPurchases={tokenPurchases}
        {...ActivityProps}
      />
    ));
  }
}

const rootCss = ({ spacing }) => ({
  '.CC-noFavorites': {
    textAlign: 'center',
    padding: spacing(4),
  },
  '.CC-bookmarkIcon': {
    margin: spacing(0, 0.5),
  },
});
